import { useState } from 'react';
import {
  MapPin,
  Monitor,
  CreditCard,
  ExternalLink,
  AlertCircle,
  CheckCircle,
  Loader2,
  TrendingUp,
  Plus,
  Minus,
  Infinity,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useSubscriptionInfo,
  useLicenseUsage,
  useOpenBillingPortal,
  useLicenseChange,
  usePlanPricing,
} from '@/hooks/useBilling';
import { cn } from '@/lib/utils';

interface BillingSectionProps {
  showToast: (type: 'success' | 'error', message: string) => void;
}

interface PendingChanges {
  locations: number; // delta from current licensed count
  registers: number; // delta from current licensed count
}

interface LicenseChangeConfirmation {
  locationDelta: number;
  registerDelta: number;
  currentLocations: number;
  currentRegisters: number;
  newLocations: number;
  newRegisters: number;
  locationPriceImpact: number;
  registerPriceImpact: number;
  totalPriceImpact: number;
}

export function BillingSection({ showToast }: BillingSectionProps) {
  const { data: subscription, isLoading: subscriptionLoading } = useSubscriptionInfo();
  const { data: usage, isLoading: usageLoading } = useLicenseUsage();
  const { data: pricing } = usePlanPricing();
  const openPortal = useOpenBillingPortal();
  const licenseChange = useLicenseChange();

  const [confirmDialog, setConfirmDialog] = useState<LicenseChangeConfirmation | null>(null);
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({ locations: 0, registers: 0 });

  const isLoading = subscriptionLoading || usageLoading;

  // Calculate preview counts based on pending changes
  const previewLocations = (usage?.licensed_locations || 1) + pendingChanges.locations;
  const previewRegisters = (usage?.licensed_registers || 2) + pendingChanges.registers;

  // Check if there are any pending changes
  const hasPendingChanges = pendingChanges.locations !== 0 || pendingChanges.registers !== 0;

  // Check if plan has unlimited resources (very high limits indicate unlimited)
  const UNLIMITED_THRESHOLD = 100;
  const hasUnlimitedLocations = (usage?.licensed_locations || 0) >= UNLIMITED_THRESHOLD;
  const hasUnlimitedRegisters = (usage?.licensed_registers || 0) >= UNLIMITED_THRESHOLD;
  const isPro = subscription?.plan_name === 'Pro' || subscription?.plan_name === 'Enterprise';

  // Get pricing for additional resources
  const locationPrice = pricing?.per_location_price || 40;
  const registerPrice = pricing?.per_register_price || 15;

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; label: string }> = {
      active: { bg: 'bg-green-500/10', text: 'text-green-600', label: 'Active' },
      trialing: { bg: 'bg-blue-500/10', text: 'text-blue-600', label: 'Trial' },
      past_due: { bg: 'bg-orange-500/10', text: 'text-orange-600', label: 'Past Due' },
      cancelled: { bg: 'bg-gray-500/10', text: 'text-gray-600', label: 'Cancelled' },
      unpaid: { bg: 'bg-red-500/10', text: 'text-red-600', label: 'Unpaid' },
    };
    const style = styles[status] || styles.active;
    return (
      <Badge variant="outline" className={cn(style.bg, style.text, 'border-transparent')}>
        {style.label}
      </Badge>
    );
  };

  const handleOpenPortal = async () => {
    try {
      await openPortal.mutateAsync();
    } catch {
      showToast('error', 'Failed to open billing portal');
    }
  };

  const handleLicenseChangeRequest = (type: 'location' | 'register', delta: number) => {
    if (type === 'location') {
      const newPendingDelta = pendingChanges.locations + delta;
      const newCount = (usage?.licensed_locations || 1) + newPendingDelta;
      // Don't allow going below minimum (1) or below active usage
      if (newCount < 1 || newCount < (usage?.active_locations || 0)) return;
      setPendingChanges(prev => ({ ...prev, locations: newPendingDelta }));
    } else {
      const newPendingDelta = pendingChanges.registers + delta;
      const newCount = (usage?.licensed_registers || 2) + newPendingDelta;
      // Don't allow going below minimum (1) or below active usage
      if (newCount < 1 || newCount < (usage?.active_registers || 0)) return;
      setPendingChanges(prev => ({ ...prev, registers: newPendingDelta }));
    }
  };

  const handleApplyChanges = () => {
    const currentLocations = usage?.licensed_locations || 1;
    const currentRegisters = usage?.licensed_registers || 2;
    const newLocations = currentLocations + pendingChanges.locations;
    const newRegisters = currentRegisters + pendingChanges.registers;
    const locationPriceImpact = pendingChanges.locations * locationPrice;
    const registerPriceImpact = pendingChanges.registers * registerPrice;

    setConfirmDialog({
      locationDelta: pendingChanges.locations,
      registerDelta: pendingChanges.registers,
      currentLocations,
      currentRegisters,
      newLocations,
      newRegisters,
      locationPriceImpact,
      registerPriceImpact,
      totalPriceImpact: locationPriceImpact + registerPriceImpact,
    });
  };

  const handleCancelPendingChanges = () => {
    setPendingChanges({ locations: 0, registers: 0 });
  };

  const handleConfirmLicenseChange = async () => {
    if (!confirmDialog) return;

    try {
      // Apply location changes if any
      if (confirmDialog.locationDelta !== 0) {
        await licenseChange.mutateAsync({
          resourceType: 'location',
          quantity: confirmDialog.locationDelta,
        });
      }

      // Apply register changes if any
      if (confirmDialog.registerDelta !== 0) {
        await licenseChange.mutateAsync({
          resourceType: 'register',
          quantity: confirmDialog.registerDelta,
        });
      }

      // Build success message
      const changes: string[] = [];
      if (confirmDialog.locationDelta !== 0) {
        const action = confirmDialog.locationDelta > 0 ? 'added' : 'removed';
        changes.push(`${action} ${Math.abs(confirmDialog.locationDelta)} location${Math.abs(confirmDialog.locationDelta) > 1 ? 's' : ''}`);
      }
      if (confirmDialog.registerDelta !== 0) {
        const action = confirmDialog.registerDelta > 0 ? 'added' : 'removed';
        changes.push(`${action} ${Math.abs(confirmDialog.registerDelta)} register${Math.abs(confirmDialog.registerDelta) > 1 ? 's' : ''}`);
      }

      showToast('success', `Successfully ${changes.join(' and ')}`);
      setPendingChanges({ locations: 0, registers: 0 });
      setConfirmDialog(null);
    } catch (error) {
      showToast('error', error instanceof Error ? error.message : 'Failed to update license');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const locationUsagePercent = usage
    ? Math.min((usage.active_locations / usage.licensed_locations) * 100, 100)
    : 0;
  const registerUsagePercent = usage
    ? Math.min((usage.active_registers / usage.licensed_registers) * 100, 100)
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Billing & Subscription</h3>
        <p className="text-sm text-muted-foreground">
          Manage your subscription plan and licenses
        </p>
      </div>

      {/* Plan Overview */}
      <div className="p-6 rounded-xl border bg-gradient-to-br from-primary/5 to-primary/10">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h4 className="text-xl font-semibold">{subscription?.plan_name || 'Entry'} Plan</h4>
              {getStatusBadge(subscription?.status || 'active')}
            </div>
            <p className="text-sm text-muted-foreground">
              {subscription?.current_period_end
                ? `Renews on ${new Date(subscription.current_period_end).toLocaleDateString()}`
                : 'Your current subscription plan'}
            </p>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleOpenPortal}
            disabled={openPortal.isPending}
          >
            {openPortal.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <TrendingUp className="h-4 w-4" />
            )}
            Upgrade Plan
          </Button>
        </div>
      </div>

      {/* License Management */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Locations */}
        <div className={cn(
          "p-5 rounded-xl border bg-card",
          pendingChanges.locations !== 0 && "ring-2 ring-primary/50"
        )}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h5 className="font-medium">Locations</h5>
                <p className="text-sm text-muted-foreground">Outlets / Stores</p>
              </div>
            </div>
            {/* +/- Buttons */}
            {!hasUnlimitedLocations && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleLicenseChangeRequest('location', -1)}
                  disabled={previewLocations <= 1 || previewLocations <= (usage?.active_locations || 0) || licenseChange.isPending}
                  title="Remove location"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-8 text-center font-semibold tabular-nums">
                  {previewLocations}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleLicenseChangeRequest('location', 1)}
                  disabled={licenseChange.isPending}
                  title={`Add location (+${formatCurrency(locationPrice)}/mo)`}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
            {hasUnlimitedLocations && (
              <Badge variant="secondary" className="gap-1">
                <Infinity className="h-3 w-3" />
                Unlimited
              </Badge>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>
                <span className="font-semibold text-lg">{usage?.active_locations || 0}</span>
                <span className="text-muted-foreground">
                  {hasUnlimitedLocations ? ' active' : ` / ${usage?.licensed_locations || 1}`}
                  {pendingChanges.locations !== 0 && (
                    <span className={cn(
                      "ml-1",
                      pendingChanges.locations > 0 ? "text-green-600" : "text-orange-600"
                    )}>
                      {pendingChanges.locations > 0 ? '+' : ''}{pendingChanges.locations}
                    </span>
                  )}
                </span>
              </span>
              {!hasUnlimitedLocations && (
                <span className="text-muted-foreground">
                  {previewLocations - (usage?.active_locations || 0)} available
                </span>
              )}
            </div>
            {!hasUnlimitedLocations && (
              <>
                <Progress value={locationUsagePercent} className="h-2" />
                {locationUsagePercent >= 100 && pendingChanges.locations === 0 && (
                  <p className="text-xs text-orange-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Location limit reached
                  </p>
                )}
                {pendingChanges.locations !== 0 && (
                  <p className={cn(
                    "text-xs flex items-center gap-1",
                    pendingChanges.locations > 0 ? "text-green-600" : "text-orange-600"
                  )}>
                    {pendingChanges.locations > 0 ? '+' : ''}{formatCurrency(pendingChanges.locations * locationPrice)}/mo
                  </p>
                )}
                {pendingChanges.locations === 0 && (
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(locationPrice)}/mo per additional location
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Registers */}
        <div className={cn(
          "p-5 rounded-xl border bg-card",
          pendingChanges.registers !== 0 && "ring-2 ring-primary/50"
        )}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-500/10">
                <Monitor className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h5 className="font-medium">Registers</h5>
                <p className="text-sm text-muted-foreground">POS Terminals</p>
              </div>
            </div>
            {/* +/- Buttons */}
            {!hasUnlimitedRegisters && (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleLicenseChangeRequest('register', -1)}
                  disabled={previewRegisters <= 1 || previewRegisters <= (usage?.active_registers || 0) || licenseChange.isPending}
                  title="Remove register"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-8 text-center font-semibold tabular-nums">
                  {previewRegisters}
                </span>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => handleLicenseChangeRequest('register', 1)}
                  disabled={licenseChange.isPending}
                  title={`Add register (+${formatCurrency(registerPrice)}/mo)`}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
            {hasUnlimitedRegisters && (
              <Badge variant="secondary" className="gap-1">
                <Infinity className="h-3 w-3" />
                Unlimited
              </Badge>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>
                <span className="font-semibold text-lg">{usage?.active_registers || 0}</span>
                <span className="text-muted-foreground">
                  {hasUnlimitedRegisters ? ' active' : ` / ${usage?.licensed_registers || 2}`}
                  {pendingChanges.registers !== 0 && (
                    <span className={cn(
                      "ml-1",
                      pendingChanges.registers > 0 ? "text-green-600" : "text-orange-600"
                    )}>
                      {pendingChanges.registers > 0 ? '+' : ''}{pendingChanges.registers}
                    </span>
                  )}
                </span>
              </span>
              {!hasUnlimitedRegisters && (
                <span className="text-muted-foreground">
                  {previewRegisters - (usage?.active_registers || 0)} available
                </span>
              )}
            </div>
            {!hasUnlimitedRegisters && (
              <>
                <Progress value={registerUsagePercent} className="h-2" />
                {registerUsagePercent >= 100 && pendingChanges.registers === 0 && (
                  <p className="text-xs text-orange-600 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Register limit reached
                  </p>
                )}
                {pendingChanges.registers !== 0 && (
                  <p className={cn(
                    "text-xs flex items-center gap-1",
                    pendingChanges.registers > 0 ? "text-green-600" : "text-orange-600"
                  )}>
                    {pendingChanges.registers > 0 ? '+' : ''}{formatCurrency(pendingChanges.registers * registerPrice)}/mo
                  </p>
                )}
                {pendingChanges.registers === 0 && (
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(registerPrice)}/mo per additional register
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Pending Changes Summary and Apply Button */}
      {hasPendingChanges && (
        <div className="p-4 rounded-xl border border-primary/50 bg-primary/5">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium">Pending Changes</h4>
              <div className="text-sm text-muted-foreground mt-1 space-y-0.5">
                {pendingChanges.locations !== 0 && (
                  <p>
                    Locations: {usage?.licensed_locations || 1} &rarr; {previewLocations}
                    <span className={cn(
                      "ml-2",
                      pendingChanges.locations > 0 ? "text-green-600" : "text-orange-600"
                    )}>
                      ({pendingChanges.locations > 0 ? '+' : ''}{formatCurrency(pendingChanges.locations * locationPrice)}/mo)
                    </span>
                  </p>
                )}
                {pendingChanges.registers !== 0 && (
                  <p>
                    Registers: {usage?.licensed_registers || 2} &rarr; {previewRegisters}
                    <span className={cn(
                      "ml-2",
                      pendingChanges.registers > 0 ? "text-green-600" : "text-orange-600"
                    )}>
                      ({pendingChanges.registers > 0 ? '+' : ''}{formatCurrency(pendingChanges.registers * registerPrice)}/mo)
                    </span>
                  </p>
                )}
              </div>
              <p className={cn(
                "text-sm font-medium mt-2",
                (pendingChanges.locations * locationPrice + pendingChanges.registers * registerPrice) > 0
                  ? "text-green-600"
                  : "text-orange-600"
              )}>
                Total billing change: {(pendingChanges.locations * locationPrice + pendingChanges.registers * registerPrice) > 0 ? '+' : ''}
                {formatCurrency(pendingChanges.locations * locationPrice + pendingChanges.registers * registerPrice)}/mo
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleCancelPendingChanges}
                disabled={licenseChange.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleApplyChanges}
                disabled={licenseChange.isPending}
              >
                Apply Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Need More Licenses Alert */}
      {!isPro && (locationUsagePercent >= 80 || registerUsagePercent >= 80) && (
        <div className="p-4 rounded-xl border border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-800">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-orange-800 dark:text-orange-200">
                Running low on licenses
              </h4>
              <p className="text-sm text-orange-700 dark:text-orange-300 mt-1">
                You're approaching your license limits. Use the + buttons above to add more,
                or upgrade to Pro for unlimited locations and registers.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 gap-2"
                onClick={handleOpenPortal}
              >
                <TrendingUp className="h-4 w-4" />
                Upgrade to Pro
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Method */}
      <div className="p-5 rounded-xl border bg-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CreditCard className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h5 className="font-medium">Payment Method</h5>
              <p className="text-sm text-muted-foreground">
                Manage your payment methods and billing info
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            className="gap-2"
            onClick={handleOpenPortal}
            disabled={openPortal.isPending}
          >
            <ExternalLink className="h-4 w-4" />
            Manage
          </Button>
        </div>
      </div>

      {/* Billing Portal Link */}
      <div className="p-4 rounded-xl border bg-muted/30">
        <div className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="font-medium">Billing Portal</h4>
            <p className="text-sm text-muted-foreground mt-1">
              View invoices, update payment methods, and manage your subscription through our secure
              billing portal powered by Stripe.
            </p>
            <Button
              variant="link"
              className="px-0 gap-1 h-auto mt-2"
              onClick={handleOpenPortal}
              disabled={openPortal.isPending}
            >
              Open Billing Portal
              <ExternalLink className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>

      {/* License Change Confirmation Dialog */}
      <Dialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm License Changes</DialogTitle>
            <DialogDescription>
              Review the changes to your subscription below. Your card will be charged or credited accordingly.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-1">
            {/* Locations changes */}
            {confirmDialog?.locationDelta !== 0 && (
              <div className="flex items-center justify-between py-3 border-b">
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-purple-600" />
                  <span className="text-muted-foreground">Locations</span>
                </div>
                <div className="text-right">
                  <span className="font-medium">
                    {confirmDialog?.currentLocations} &rarr; {confirmDialog?.newLocations}
                  </span>
                  <span className={cn(
                    "ml-2 text-sm",
                    (confirmDialog?.locationPriceImpact || 0) > 0 ? "text-green-600" : "text-orange-600"
                  )}>
                    ({(confirmDialog?.locationPriceImpact || 0) > 0 ? '+' : ''}{formatCurrency(confirmDialog?.locationPriceImpact || 0)}/mo)
                  </span>
                </div>
              </div>
            )}

            {/* Registers changes */}
            {confirmDialog?.registerDelta !== 0 && (
              <div className="flex items-center justify-between py-3 border-b">
                <div className="flex items-center gap-2">
                  <Monitor className="h-4 w-4 text-orange-600" />
                  <span className="text-muted-foreground">Registers</span>
                </div>
                <div className="text-right">
                  <span className="font-medium">
                    {confirmDialog?.currentRegisters} &rarr; {confirmDialog?.newRegisters}
                  </span>
                  <span className={cn(
                    "ml-2 text-sm",
                    (confirmDialog?.registerPriceImpact || 0) > 0 ? "text-green-600" : "text-orange-600"
                  )}>
                    ({(confirmDialog?.registerPriceImpact || 0) > 0 ? '+' : ''}{formatCurrency(confirmDialog?.registerPriceImpact || 0)}/mo)
                  </span>
                </div>
              </div>
            )}

            {/* Total */}
            <div className="flex items-center justify-between py-3">
              <span className="font-medium">Total Monthly Change</span>
              <span className={cn(
                'font-semibold text-lg',
                (confirmDialog?.totalPriceImpact || 0) > 0 ? 'text-green-600' : 'text-orange-600'
              )}>
                {(confirmDialog?.totalPriceImpact || 0) > 0 ? '+' : ''}
                {formatCurrency(confirmDialog?.totalPriceImpact || 0)}/mo
              </span>
            </div>
          </div>

          {(confirmDialog?.totalPriceImpact || 0) > 0 && (
            <p className="text-sm text-muted-foreground">
              Your card will be charged a prorated amount for the current billing period.
            </p>
          )}

          {(confirmDialog?.totalPriceImpact || 0) < 0 && (
            <p className="text-sm text-muted-foreground">
              Your account will be credited. The credit will be applied to future invoices.
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmLicenseChange}
              disabled={licenseChange.isPending}
              variant={(confirmDialog?.totalPriceImpact || 0) < 0 ? 'destructive' : 'default'}
            >
              {licenseChange.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Confirm Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
